type Partish = { qty?: number; unitBuyPrice?: number; totalBuyPrice?: number };

const toCents = (n: number): number => Math.round(n * 100);

function rowQtyTimesUnitEqualsTotal(part: Partish | undefined): boolean {
    if (!part) return false;
    const qty = typeof part.qty === 'number' ? part.qty : 0;
    const unit = typeof part.unitBuyPrice === 'number' ? part.unitBuyPrice : 0;
    const total = typeof part.totalBuyPrice === 'number' ? part.totalBuyPrice : 0;
    return toCents(qty * unit) === toCents(total);
}

/** Same tab order as `checkArithmeticIssuesAllTabs` / UI partIndex (excludes ignored tab). */
function resolvePartAtArithmeticIndex(
    tabs: {
        lineItems: readonly Partish[];
        freightItems?: readonly Partish[];
        fuelItems?: readonly Partish[];
        restockingFeeItems?: readonly Partish[];
    },
    globalIndex: number
): Partish | undefined {
    const segments = [
        tabs.lineItems,
        tabs.freightItems ?? [],
        tabs.fuelItems ?? [],
        tabs.restockingFeeItems ?? [],
    ];
    let offset = 0;
    for (const seg of segments) {
        if (globalIndex >= offset && globalIndex < offset + seg.length) {
            return seg[globalIndex - offset];
        }
        offset += seg.length;
    }
    return undefined;
}

/**
 * `partIndex` is global across line, freight, fuel, misc fees, then ignored.
 * Arithmetic checks must not apply to ignored rows; strip stale calculation_mismatch flags for that segment.
 * Also drops calculation_mismatch when the row now reconciles (e.g. freight buy price normalized on read).
 */
export function filterOutIgnoredTabCalculationMismatches<T extends { issueType?: string; partIndex?: number }>(
    flaggedParts: T[] | undefined,
    tabs: {
        lineItems: readonly unknown[];
        freightItems?: readonly unknown[];
        fuelItems?: readonly unknown[];
        restockingFeeItems?: readonly unknown[];
        ignoredParts?: readonly unknown[];
    }
): T[] {
    if (!flaggedParts?.length) return [];
    const {
        lineItems,
        freightItems = [],
        fuelItems = [],
        restockingFeeItems = [],
        ignoredParts = [],
    } = tabs;
    const ignoredStart =
        lineItems.length +
        freightItems.length +
        fuelItems.length +
        restockingFeeItems.length;
    const arithTabs = {
        lineItems: lineItems as Partish[],
        freightItems: freightItems as Partish[],
        fuelItems: fuelItems as Partish[],
        restockingFeeItems: restockingFeeItems as Partish[],
    };
    return flaggedParts.filter((issue) => {
        if (issue.issueType !== 'calculation_mismatch') return true;
        const idx = issue.partIndex;
        if (typeof idx !== 'number') return true;
        if (idx >= ignoredStart && idx < ignoredStart + ignoredParts.length) return false;
        const part = resolvePartAtArithmeticIndex(arithTabs, idx);
        if (rowQtyTimesUnitEqualsTotal(part)) return false;
        return true;
    });
}
